@if (loading) {
  <div class="state-panel state-loading">
    <div class="state-title">Loading responses</div>
    <div class="state-copy">Fetching the current list and status details.</div>
    <div class="loading-list" aria-hidden="true">
      @for (slot of [1, 2, 3]; track slot) {
        <div class="loading-row">
          <span class="loading-block loading-actions"></span>
          <span class="loading-block loading-received"></span>
          <span class="loading-block loading-name"></span>
          <span class="loading-block loading-contact"></span>
          <span class="loading-block loading-status"></span>
        </div>
      }
    </div>
  </div>
} @else {
  <div class="responses-toolbar">
    <div class="toolbar-group">
      <button class="nav" (click)="toggleShowAll()">
        @if (showAll) {
          Hide Empty Responses
        } @else {
          Show All Responses
        }
      </button>
      <button class="nav" (click)="downloadAsCSV()">
        <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#download"></use></svg>
        Download CSV
      </button>
    </div>
    <div class="toolbar-group toolbar-filters">
      <label>
        Sort
        <select [(ngModel)]="sortMode" name="responseSort" (ngModelChange)="onFilterChange()">
          <option value="received-desc">Newest first</option>
          <option value="received-asc">Oldest first</option>
        </select>
      </label>
      <label>
        Status
        <select [(ngModel)]="statusFilter" name="responseStatus" (ngModelChange)="onFilterChange()">
          <option value="all">All responses</option>
          <option value="published">Published only</option>
          <option value="draft">Unpublished only</option>
        </select>
      </label>
      @if (hasActiveFilters) {
        <button class="nav" (click)="resetFilters()">Clear filters</button>
      }
    </div>
  </div>

  @if (publishMessage) {
    <div
      class="publish-notice"
      [class.publish-notice-error]="publishMessageIsError"
      [attr.role]="publishMessageIsError ? 'alert' : 'status'"
      aria-live="polite">
      {{publishMessage}}
    </div>
  }

  @if (errorMessage) {
    <div class="state-panel state-error">
      <div class="state-title">Could not load responses</div>
      <div class="state-copy">{{errorMessage}}</div>
      <button class="nav" (click)="refreshResponses()">Retry</button>
    </div>
  } @else {
    @if (hasMatchingResponses) {
      <div class="responses-summary">
        Showing {{responses.length}} of {{allResponses.length}} responses
      </div>
      <div class="responses_header">
        <span class="received">Received</span>
        <span class="name">Response</span>
        <span class="duration">Secs</span>
        <span class="contact">Contact details</span>
        <span class="status">Status</span>
      </div>
      <div id="responses">
        @for (response of responses; track response.id) {
          <div
            [style.background-color]="this.getColorFor(response)"
            (click)="onSelect(response)"
            class="response">
            <div class="response-body" (click)="onSelect(response)">
              <div class="identity-row">
                <span class="name">
                  <span class="identity-name">{{response.name}}</span>
                  @if (!!response.location) {
                    <span class="identity-location">from {{response.location}}</span>
                  }
                </span>
                <span class="received">
                  <span class="card-label">Received</span>
                  <span class="date">{{response.received | smalltime}}</span>
                </span>
              </div>

              <div class="metadata-row">
                <span class="topic-line">
                  <span class="card-label">Topic</span>
                  <span class="topic-tag">{{response.topic_short_name}}</span>
                </span>
                <span class="duration">
                  <span class="card-label">Duration</span>
                  <span>{{response.duration | secs}}</span>
                </span>
              </div>

              <span class="status-group" aria-label="Response status">
                <span
                  class="status-badge"
                  [class.status-published]="response.published && !isPublishing(response)"
                  [class.status-draft]="!response.published && !isPublishing(response)"
                  [class.status-updating]="isPublishing(response)">
                  {{publishLabel(response)}}
                </span>
                <span class="status-badge" [class.status-transcript]="hasTranscript(response)" [class.status-muted]="!hasTranscript(response)">
                  {{transcriptLabel(response)}}
                </span>
                <span class="status-badge" [class.status-audio]="audioStatus(response) === 'available'" [class.status-muted]="audioStatus(response) !== 'available'">
                  {{audioLabel(response)}}
                </span>
              </span>

              <div class="transcript">
                <span class="card-label transcript-label">Transcript</span>
                <span class="transcript-preview">
                  @if (!!response.corrected_transcript) {
                    {{response.corrected_transcript}}
                  } @else {
                    {{response.transcript}}
                  }
                </span>
              </div>

              @if (!!response.email || !!response.phone) {
                <span class="contact">
                  <span class="card-label">Contact</span>
                  <span class="contact-values">
                    @if (!!response.email) {
                      <a href="mailto:{{response.email}}">{{response.email}}</a>
                    }
                    @if (!!response.email && !!response.phone) {
                      <span aria-hidden="true">, </span>
                    }
                    @if (!!response.phone) {
                      <a href="tel:{{response.phone}}">{{response.phone}}</a>
                    }
                  </span>
                </span>
              } @else {
                <span class="contact contact-empty">
                  <span class="card-label">Contact</span>
                  <span>No contact details</span>
                </span>
              }
            </div>

            <div class="actions" aria-label="Response actions">
              <a
                class="action-control"
                href="/api/private/r/audio/{{response.id}}/m4a/{{response.topic}}-{{response.id}}-{{response.name}}.m4a"
                download="{{response.topic}}-{{response.id}}-{{response.name}}.m4a"
                target="_blank"
                title="Download audio"
                aria-label="Download audio">
                <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#download"></use></svg>
                <span class="action-label">Download</span>
              </a>
              <button type="button" class="action-control" title="Open player" aria-label="Open player" (click)="onSelect(response)">
                <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#open"></use></svg>
                <span class="action-label">Open</span>
              </button>
              <button
                type="button"
                class="action-control"
                [title]="publishActionTitle(response)"
                [attr.aria-label]="publishActionTitle(response)"
                [attr.aria-busy]="isPublishing(response)"
                [disabled]="isPublishing(response)"
                (click)="publishResponse(response); $event.stopPropagation()">
                <svg class="cms-icon" aria-hidden="true" focusable="false"><use [attr.href]="response.published ? '/images/cms-actions.svg#unpublish' : '/images/cms-actions.svg#publish'"></use></svg>
                <span class="action-label">{{publishActionLabel(response)}}</span>
              </button>
              <button type="button" class="action-control" title="Responses by person" aria-label="Responses by person" (click)="personResponses(response)">
                <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#person"></use></svg>
                <span class="action-label">Responses by person</span>
              </button>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="state-panel state-empty">
        <div class="state-title">
          @if (hasLoadedResponses) {
            No responses match the current filters
          } @else {
            No responses in range
          }
        </div>
        <div class="state-copy">
          @if (hasLoadedResponses) {
            Try widening the list, changing the status filter, or showing empty responses.
          } @else {
            This queue has no responses for the current range yet.
          }
        </div>
        @if (hasLoadedResponses || hasActiveFilters) {
          <button class="nav" (click)="resetFilters()">Reset view</button>
        }
      </div>
    }
  }

  <div class="responses-pagination">
    <span class="actions"><button (click)="earlier()" class="nav">Older</button></span>
  </div>
  @if (currentResponse) {
    <div id="currentResponsePlayer" class="player">
      <response-player [response]="currentResponse"
        (responseChange)="transcriptUpdated($event)"
        [observableResponse]="currentResponseObservable"
        [publishUpdating]="isPublishing(currentResponse)"
        [publishActionLabel]="publishActionLabel(currentResponse)"
        [publishActionTitle]="publishActionTitle(currentResponse)"
        [publishMessage]="publishMessageResponseId === currentResponse.id ? publishMessage : null"
        [publishMessageIsError]="publishMessageIsError"
        (publishToggle)="publishResponse($event)"
        (cancelPlayer)="cancelPlayer($event)"></response-player>
    </div>
  }
}
